/**
 * What the lead watch is allowed to do: four numbers and one switch, in the
 * panel the Rikked tab's engine strip opens from its gear.
 *
 * Numbers are edited locally and committed on blur (or Enter), clamped to the
 * server's limits, so typing "2" on the way to "20" never saves a value nobody
 * meant. A save that fails puts the stored value back in the field
 * (`numberDraft.ts`), which is why this form keeps its own field rather than
 * the autopilots' shared one. The switch writes straight through, as the
 * strip's main switch does.
 */
import { useEffect, useId, useReducer } from "react";
import { Input, Label, Switch } from "@wa/ui";
import {
  DOMAIN_EXPIRY_WINDOW_DAYS,
  SITE_WATCH_LIMITS,
  SITE_WATCH_NUMBER_FIELDS,
  commitWatchNumber,
  type SiteWatchNumberField,
  type SiteWatchSettings,
} from "../../../../lib/siteWatch";
import type { T } from "../../site-scanner/types";
import { numberDraft, numberDraftReducer } from "./numberDraft";

function NumberField({
  field,
  value,
  disabled,
  onCommit,
  t,
}: {
  field: SiteWatchNumberField;
  value: number;
  disabled: boolean;
  /** Resolves true once the value is saved. */
  onCommit: (value: number) => Promise<boolean>;
  t: T;
}) {
  const id = useId();
  const [draft, dispatch] = useReducer(numberDraftReducer, value, numberDraft);
  useEffect(() => dispatch({ type: "stored", value }), [value]);
  const limits = SITE_WATCH_LIMITS[field];

  const commit = () => {
    const next = commitWatchNumber(field, draft.text, value);
    const text = String(next.value);
    dispatch({ type: "typed", text });
    if (!next.changed) return;
    void onCommit(next.value).then((saved) => {
      if (!saved) dispatch({ type: "refused", committed: text });
    });
  };

  return (
    // `min-w-0`: a grid track sizes itself to its content, and a label with a
    // long hint would otherwise push its track wider than the card.
    <div className="min-w-0">
      <Label htmlFor={id} className="crm-lab !mt-0 text-[12.5px] font-semibold">
        {t(`leads.watch.field.${field}`)}
      </Label>
      <Input
        id={id}
        value={draft.text}
        inputMode="numeric"
        disabled={disabled}
        onChange={(e) => dispatch({ type: "typed", text: e.target.value })}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        className="h-11"
      />
      <span className="crm-sub mt-1.5 block text-[11px] leading-snug">
        {t(`leads.watch.hint.${field}`)} {t("leads.watch.range", { min: limits.min, max: limits.max })}
      </span>
    </div>
  );
}

export function WatchSettings({
  settings,
  canManage,
  saving,
  onPatch,
  t,
}: {
  settings: SiteWatchSettings;
  canManage: boolean;
  saving: boolean;
  /** Resolves true once the change is saved. */
  onPatch: (change: Partial<SiteWatchSettings>) => Promise<boolean>;
  t: T;
}) {
  const locked = !canManage || saving;
  const expiryId = useId();

  return (
    // Two columns: this is a side panel now, and four numbers in one row there
    // leave each hint two words wide.
    <div className="mt-5 space-y-5 border-t border-[var(--crm-hair)] px-[26px] pb-6 pt-5">
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
        {SITE_WATCH_NUMBER_FIELDS.map((field) => (
          <NumberField
            key={field}
            field={field}
            value={settings[field]}
            disabled={locked}
            onCommit={(value) => {
              const change: Partial<SiteWatchSettings> = {};
              change[field] = value;
              return onPatch(change);
            }}
            t={t}
          />
        ))}
      </div>

      <div className="flex items-start gap-3">
        <Switch
          id={expiryId}
          checked={settings.domainExpiry}
          disabled={locked}
          onCheckedChange={(checked) => void onPatch({ domainExpiry: checked })}
          className="mt-0.5"
        />
        <span className="min-w-0">
          <Label htmlFor={expiryId} className="block text-[13px] font-[550] leading-snug text-foreground">
            {t("leads.watch.field.domainExpiry")}
          </Label>
          <span className="crm-sub block text-[11.5px] leading-snug">
            {t("leads.watch.hint.domainExpiry", { days: DOMAIN_EXPIRY_WINDOW_DAYS })}
          </span>
        </span>
      </div>
    </div>
  );
}
